import { LegalLink, LegalSection, LegalShell, SupportContact } from "./LegalShell.js";
import { useSite } from "./site.js";

/**
 * Deleting an account and asking for your data (GH-812).
 *
 * App Store Guideline 5.1.1(v) asks for a deletion path a reviewer can reach from outside the app;
 * this is the page App Store Connect links to. What it says is deleted and kept is what
 * `donors.deleteAccount` does and what the in-app confirmation already says — not a summary of
 * it, so the two cannot disagree about what survives.
 */
export function AccountDeletionPage() {
  const { productName: product } = useSite();
  return (
    <LegalShell title="Delete your account or ask for your data">
      <LegalSection id="in-app" title="Delete your account in the app">
        <ol>
          <li>Open {product} and go to Account.</li>
          <li>Open Profile &amp; privacy.</li>
          <li>Choose Delete my account, then confirm.</li>
        </ol>
        <p>
          Deletion happens at once and signs you out. You do not need an account to give, so you can
          still give afterwards.
        </p>
      </LegalSection>

      <LegalSection id="without-app" title="Without the app">
        <p>
          Contact <SupportContact /> from the email address or mobile number on your account, and
          ask us to delete it. We will confirm it is you before deleting it. We confirm it is you by
          sending a one-time code to that email address or mobile number, and we delete the account
          within 30 days of confirming.
        </p>
      </LegalSection>

      <LegalSection id="deleted" title="What is deleted">
        <ul>
          <li>Your mobile number and email address.</li>
          <li>Your name and mailing address.</li>
          <li>Your saved cards and bank accounts, which are also removed from Stripe and Plaid.</li>
          <li>Your sign-in sessions on every device.</li>
          <li>Your event check-ins, reactions, and answers to prompts.</li>
        </ul>
      </LegalSection>

      <LegalSection id="kept" title="What is kept">
        <p>
          Receipts and gift records for gifts you already made are kept as financial records, as the
          law requires. See{" "}
          <LegalLink to="privacy" hash="retention">
            how long we keep it
          </LegalLink>
          .
        </p>
      </LegalSection>

      <LegalSection id="requests" title="Other requests">
        <p>
          To see, correct, or get a copy of your information, or to ask any privacy question,
          contact <SupportContact />. The <LegalLink to="privacy">Privacy Policy</LegalLink> lists
          your rights.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
